import { useCallback, useEffect, useState } from "react";
import KorrekturService from "../korrektur.service";
import { Button } from "@/components/ui/button";
import NutrientPDFViewer from "../../../components/nutrientPDFViewer.component";
import { AnnotationEventsProvider } from "@/lib/annotations/AnnotationEvents.context";
import { toast } from "sonner";

interface PDFViewerProps {
	korrekturStudentKlausurId: string;
	korrekturStepId: string;
	annotations: InstantJSON | null;
	className?: string;
	// this is the color the user will be annotating in
	inkColorHex: string;
	/** When true, load finished PDF and disable annotations */
	readOnly?: boolean;
	/** When true, keep PDF but disable drawing/annotation tools */
	disableDrawing?: boolean;
}

/**
 * This component is used to display the PDF viewer for the korrektur using nutrient pdf viewer
 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
 * @param korrekturStepId - The id of the korrektur step
 * @param annotations - The annotations for the korrektur
 * @param className - The class name for the component
 * @param inkColorHex - The color the user will be annotating in (server defined)
 */
export default function PDFViewer({
	korrekturStudentKlausurId,
	korrekturStepId,
	annotations,
	className = "",
	inkColorHex,
	readOnly = false,
	disableDrawing = false,
}: PDFViewerProps) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [documentUrl, setDocumentUrl] = useState<string | null>(null);
	const viewerReadOnly = readOnly || disableDrawing;

	/**
	 * Load the PDF for the korrektur
	 */
	useEffect(() => {
		let cancelled = false;

		/**
		 * This functions loads the PDF for the korrektur
		 */
		const loadPDF = async () => {
			try {
				setIsLoading(true);
				setError(null);

				// Fetch PDF blob with proper authentication
				const korrekturService = KorrekturService.getInstance();
				const pdfBlob = readOnly
					? await korrekturService.getFinishedKorrekturPdfBlob(korrekturStepId)
					: await korrekturService.getPdfBlob(korrekturStudentKlausurId);

				if (cancelled) return;

				if (pdfBlob.size === 0) {
					throw new Error("PDF blob is empty");
				}

				// Create a blob URL for the PDF
				const newDocumentUrl = URL.createObjectURL(pdfBlob);

				if (cancelled) {
					URL.revokeObjectURL(newDocumentUrl);
					return;
				}

				setDocumentUrl(newDocumentUrl);
				setIsLoading(false);
			} catch (err) {
				if (!cancelled) {
					console.error("Fehler beim Laden des PDFs:", err);
					setError(
						err instanceof Error ? err.message : "Fehler beim Laden des PDFs",
					);
					setIsLoading(false);
					throw new Error(
						err instanceof Error ? err.message : "Fehler beim Laden des PDFs",
					);
				}
			}
		};

		loadPDF();

		return () => {
			cancelled = true;
			// Cleanup will be handled when documentUrl changes
		};
	}, [korrekturStudentKlausurId, korrekturStepId, readOnly]);

	// Cleanup blob URL when component unmounts or documentUrl changes
	useEffect(() => {
		return () => {
			if (documentUrl) {
				URL.revokeObjectURL(documentUrl);
			}
		};
	}, [documentUrl]);

	/**
	 * This is called when the nutrient pdf viewer is ready
	 */
	const handleNutrientReady = useCallback(() => {
		// PDF viewer is ready, loading state is handled by NutrientPDFViewer
		console.log("Nutrient PDF viewer ready");
	}, []);

	/**
	 * This is called when the nutrient pdf viewer encounters an error
	 */
	const handleNutrientError = useCallback((errorMessage: string) => {
		setError(errorMessage);
		throw new Error(errorMessage); // throw an error to be caught by sentry
	}, []);

	/**
	 * This is called when the annotation context encounters an error
	 */
	const handleAnnotationContextError = useCallback((message: string) => {
		// Annotation upload errors are non-fatal: warn via toast, do not block the viewer
		if (message.startsWith("ANNOTATION_UPLOAD_FAILED")) {
			const cleaned = message.replace(/^ANNOTATION_UPLOAD_FAILED:\s*/, "");
			toast.error(cleaned || "Fehler beim Speichern der Annotationen");
			throw new Error(cleaned || "Fehler beim Speichern der Annotationen"); // throw an error to be caught by sentry
		}

		// For any other severe error coming from context, fall back to overlay
		setError(message);
		throw new Error(
			"Could not save annotations because of an error in the annotation context " +
				message,
		); // throw an error to be caught by sentry
	}, []);

	return (
		<div className={`w-full h-full relative z-20 ${className}`}>
			{documentUrl && !error ? (
				viewerReadOnly ? (
					<NutrientPDFViewer
						documentUrl={documentUrl}
						onReady={handleNutrientReady}
						onError={handleNutrientError}
						initialAnnotations={annotations ?? undefined}
						className="w-full h-full"
						inkColorHex={inkColorHex}
						readOnly={true}
					/>
				) : (
					<AnnotationEventsProvider
						onError={handleAnnotationContextError}
						korrekturStepId={korrekturStepId}
						korrekturStudentKlausurId={korrekturStudentKlausurId}
					>
						<NutrientPDFViewer
							documentUrl={documentUrl}
							onReady={handleNutrientReady}
							onError={handleNutrientError}
							initialAnnotations={annotations ?? undefined}
							className="w-full h-full"
							inkColorHex={inkColorHex}
						/>
					</AnnotationEventsProvider>
				)
			) : null}

			{isLoading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
					<div className="flex flex-col items-center gap-4">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
						<p className="text-sm text-gray-600">PDF laden...</p>
					</div>
				</div>
			)}

			{error && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50 p-4">
					<div className="text-center p-4 max-w-md bg-white rounded-lg shadow-lg">
						<p className="text-red-600 font-semibold mb-2">
							Fehler beim Laden des PDFs
						</p>
						<p className="text-sm text-gray-600 mb-4">{error}</p>
						<Button type="button" onClick={() => window.location.reload()}>
							Erneut versuchen
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
